<script setup>
import { computed, useAttrs } from 'vue';

/**
 * Table header cell for use in custom header rows inside HrTable.
 *
 * @status recommended
 * @group lists-tables
 * @useWhen Rendering a custom header cell in a table built with HrTable
 * @within hr-table
 * @a11y A sortable column is a tab stop, sorts on Enter or Space, and reports its direction with
 *   aria-sort. No role is added: a th already is a columnheader, and aria-sort is only valid on
 *   one, so the column announces as a sorted header rather than as a button. A cell with no
 *   column scope, or a column that is not sortable, is left untouched.
 * @see https://quasar.dev/vue-components/table Quasar q-th API
 */
// inheritAttrs: false because the template spreads $attrs onto the q-th itself. Without it
// Vue's own fallthrough applies them a second time.
defineOptions({ name: 'HrTh', inheritAttrs: false });

const ACTIVATION_KEYS = ['Enter', ' ', 'Spacebar'];

const props = defineProps({
  /**
   * The column this cell belongs to, by name.
   *
   * Quasar resolves a cell's column from its own vnode key, and `key` is consumed by Vue on THIS
   * component rather than passed down — so without it a cell in a custom header or body row cannot
   * find its column. QTh throws reading `sortable` off nothing; QTd renders an empty cell and says
   * nothing at all. Set it to `col.name` alongside the `:key` the v-for needs.
   */
  columnName: {
    type: String,
    default: null,
  },
});

const attrs = useAttrs();

const col = computed(() => attrs.props?.col ?? null);

const isSortable = computed(() => col.value?.sortable === true);

const ariaSort = computed(() => {
  if (!isSortable.value) {
    return undefined;
  }

  // eslint-disable-next-line no-underscore-dangle
  const classes = ` ${col.value.__thClass ?? ''} `;

  if (!classes.includes(' sorted ')) {
    return 'none';
  }

  return classes.includes(' sort-desc ') ? 'descending' : 'ascending';
});

function onKeydown(event) {
  if (event.target !== event.currentTarget || !ACTIVATION_KEYS.includes(event.key)) {
    return;
  }

  if (event.key !== 'Enter') {
    event.preventDefault();

    if (event.repeat === true) {
      return;
    }
  }

  event.currentTarget.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
}

const effectiveAttrs = computed(() => ({
  ...(isSortable.value ? { tabindex: 0, 'aria-sort': ariaSort.value, onKeydown } : {}),
  ...attrs,
}));
</script>

<template>
  <!-- The remaining Quasar props - `props`, `auto-width`, `colspan` - travel by $attrs,
       which is what a primitive wrapping a Quasar component is for. -->
  <q-th
    :key="props.columnName"
    v-bind="effectiveAttrs"
    :class="{ 'hr-th--sortable': isSortable }"
  >
    <!-- @slot Header cell content. -->
    <slot />
  </q-th>
</template>

<style lang="scss" scoped>
.hr-th--sortable:focus-visible {
  outline: 2px solid var(--app-primary);
  outline-offset: -2px;
}

.hr-th--sortable:focus-visible :deep(.q-table__sort-icon) {
  opacity: 0.64;
}
</style>
